import { addressSchema } from '@commenda/schema/address';
import { LegacyDocumentType } from '@prisma/client';
import * as z from 'zod';

const urlRegex = /^[\w.-]+\.[a-zA-Z]{2,}(\/[\w.-]*)*\/?$/;

export const companyInfoSchema = z
  .object({
    companyNameOption1: z
      .string()
      .trim()
      .min(1, { message: 'Please enter option 1' }),
    companyNameOption2: z
      .string()
      .trim()
      .min(1, { message: 'Please enter option 2' }),
    companyNameOption3: z
      .string()
      .trim()
      .min(1, { message: 'Please enter option 3' }),
    socialLink: z
      .string()
      .trim()
      .refine((val) => !val || urlRegex.test(val), {
        message: 'Please enter a valid URL.',
      })
      .optional(),
    primarySSIC: z.string().min(1, { message: 'Please select an option' }),
    secondarySSIC: z.string().min(1, { message: 'Please select an option' }),
    businessDescription: z.string().trim().min(25, {
      message: 'Please enter a description with at least 25 characters.',
    }),
    isBusinessInvolvedInCryptoCurrency: z
      .union([z.boolean(), z.null()])
      .refine((val) => val !== null, {
        message:
          'Please select whether your business will be involved in cryptocurrency',
      }),
    isBusinessAcceptingCryptocurrencyPayments: z.boolean(),
    doesBusinessProvideServicesToCryptoCompanies: z.boolean(),
    isBusinessManagingCryptocurrencyCoins: z.boolean(),
    hasOtherReasonForInvolvementInCrypto: z.boolean(),
    reasonForInvolvementInCrypto: z.string().trim().optional(),
    howWillBusinessBeFunded: z
      .string()
      .trim()
      .min(1, { message: 'Please select an option' }),
    reasonForIncorporatingInSingapore: z
      .string()
      .min(1, { message: 'Please select an option' }),
    customReasonForIncorporatingInSingapore: z.string().trim().optional(),
    supportedCountries: z
      .array(z.string())
      .min(1, { message: 'Please select at least one country' }),
    cryptoInteractionOptions: z.string().optional(),
    hasOptedForSGRegisteredAddress: z.boolean(),
    isMailingSameAsOperating: z.boolean(),
    operatingAddress: addressSchema.nullable().optional(),
    mailingAddress: addressSchema.nullable(),
  })
  .superRefine((data, ctx) => {
    if (data.reasonForIncorporatingInSingapore === 'Other') {
      if (!data.customReasonForIncorporatingInSingapore?.length) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Please provide reason for opening business.',
          path: ['customReasonForIncorporatingInSingapore'],
        });
      }
    }
    if (data.hasOtherReasonForInvolvementInCrypto) {
      if (!data.reasonForInvolvementInCrypto?.length) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Please provide reason for involvement in cryptocurrency.',
          path: ['reasonForInvolvementInCrypto'],
        });
      }
    }
    if (data.isBusinessInvolvedInCryptoCurrency) {
      const hasSelectedOption =
        data.isBusinessAcceptingCryptocurrencyPayments ||
        data.doesBusinessProvideServicesToCryptoCompanies ||
        data.isBusinessManagingCryptocurrencyCoins ||
        data.hasOtherReasonForInvolvementInCrypto;

      if (!hasSelectedOption) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Please select at least 1 option',
          path: ['cryptoInteractionOptions'],
        });
      }
    }
    // Validate operatingAddress only when NOT using Commenda's managed address service
    if (!data.hasOptedForSGRegisteredAddress) {
      const addr = data.operatingAddress;
      if (!addr) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Operating address is required',
          path: ['operatingAddress'],
        });
      } else {
        if (!addr.addressLine1 || addr.addressLine1.trim().length === 0) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Address line 1 is required',
            path: ['operatingAddress', 'addressLine1'],
          });
        }
        if (!addr.city || addr.city.trim().length === 0) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'City is required',
            path: ['operatingAddress', 'city'],
          });
        }
        if (!addr.postalCode || addr.postalCode.trim().length === 0) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Postal code is required',
            path: ['operatingAddress', 'postalCode'],
          });
        }
      }
    }
  });

export const directorSchema = z.object({
  directorFirstName: z
    .string()
    .trim()
    .min(1, { message: 'Please enter a valid first name' }),
  directorLastName: z.string().trim().nullable(),
  email: z
    .string()
    .trim()
    .email({ message: 'Please enter a valid email address' }),
  personalDetails: z.object({
    nationality: z.string().min(1, { message: 'Please select nationality' }),
  }),
  operatingAddress: addressSchema,
  passportProof: z.object({
    id: z.number().optional(),
    documentType: z.nativeEnum(LegacyDocumentType),
    fileName: z.string(),
    fileUrl: z.string(),
  }),
});

export type CompanyInfoFormFields = z.infer<typeof companyInfoSchema>;

export type DirectorSchemaFields = z.infer<typeof directorSchema>;
